'use client'

import { useEffect, useMemo, useState } from 'react'

import { type HeatmapColumn, placeLiveColumn } from '@/features/chart/lib/orderbook-heatmap'
import { chartBarStartMs, nextChartBarStartMs } from '@/lib/chart-timeframe'
import { useOrderbookHeatmapStream } from '@/service/ws/use-orderbook-heatmap-stream'
import type { Exchange } from '@/types/generated/common/types'
import type { OrderbookHeatmapColumn } from '@/types/generated/public_api'

/** Re-checks the open bar just after the boundary, so a late timer can't read the old bar. */
const BAR_ROLL_SLACK_MS = 50
/** setTimeout's ceiling (~24.8 days); a longer delay fires at once, which a monthly bar exceeds. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/** Start of the bar open now, kept current across bar boundaries while `enabled`. */
function useOpenBarMs(timeframe: number, enabled: boolean): number {
  const [openBarMs, setOpenBarMs] = useState(() => chartBarStartMs(Date.now(), timeframe))
  useEffect(() => {
    if (!enabled) return
    let id: ReturnType<typeof setTimeout>
    const roll = () => {
      const now = Date.now()
      const start = chartBarStartMs(now, timeframe)
      setOpenBarMs(start)
      const delay = nextChartBarStartMs(start, timeframe) - now + BAR_ROLL_SLACK_MS
      id = setTimeout(roll, Math.min(delay, MAX_TIMEOUT_MS))
    }
    roll()
    return () => clearTimeout(id)
  }, [enabled, timeframe])
  return openBarMs
}

/**
 * The heatmap's open column from `OrderbookHeatmapStream`, placed on the chart's open bar, or null
 * while there is none: before the first frame, against a backend that doesn't serve the stream,
 * while the socket is down, and once the column's bar has closed (that bar then shows its stored
 * opening snapshot until the next frame lands in the new bar).
 */
export function useLiveHeatmapColumn({
  enabled,
  exchange,
  symbol,
  timeframe,
}: {
  enabled: boolean
  exchange: Exchange
  symbol: string
  timeframe: number
}): HeatmapColumn | null {
  const active = enabled && Boolean(symbol)
  const seriesKey = `${exchange}:${symbol}`
  // Tagged with its instrument: `onClose` clears it on a symbol switch, but only after the render
  // that already carries the new symbol.
  const [latest, setLatest] = useState<{ key: string; column: OrderbookHeatmapColumn } | null>(null)
  const { connectionStatus } = useOrderbookHeatmapStream({
    connected: active,
    payload: { exchange, symbol },
    onMessage: (column) => {
      if (column) setLatest({ key: seriesKey, column })
    },
    // Switched off or torn down: the next subscribe sends the current column again.
    onClose: () => setLatest(null),
  })
  const openBarMs = useOpenBarMs(timeframe, active)

  const column =
    active && connectionStatus === 'OPEN' && latest?.key === seriesKey ? latest.column : null
  return useMemo(
    () => placeLiveColumn(column, timeframe, openBarMs),
    [column, openBarMs, timeframe]
  )
}
